import React, { useEffect, useMemo, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useCustomDispatch, useCustomLocation } from '../../hooks';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { fetchUserProfile } from '../../services/actions/auth/userProfileActions';
import {
	fetchPharmacistVerificationContext,
	savePharmacistVerification,
} from '../../services/actions/auth/pharmacistVerificationApi';
import * as routeNames from '../../routes/routeName';
import { safeToast } from '../../utils/helper/helper';

const collegeSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 46,
		borderRadius: 16,
		borderColor: state.isFocused ? '#2563eb' : '#cbd5e1',
		boxShadow: state.isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : 'none',
		paddingLeft: 6,
		backgroundColor: '#fff',
		':hover': {
			borderColor: state.isFocused ? '#2563eb' : '#94a3b8',
		},
	}),
	menu: (base) => ({
		...base,
		borderRadius: 16,
		overflow: 'hidden',
		zIndex: 220,
	}),
	menuPortal: (base) => ({
		...base,
		zIndex: 220,
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isFocused ? '#eff6ff' : '#fff',
		color: '#0f172a',
	}),
	placeholder: (base) => ({
		...base,
		color: '#64748b',
	}),
};

const PharmacistVerificationModal = ({
	open,
	formState,
	onCpsidChange,
	onCollegeChange,
	collegeOptions,
	submitting,
	hasExistingData,
	onSubmit,
}) => {
	if (!open) return null;

	const selectedCollegeOption = formState.rxcollege
		? { value: formState.rxcollege, label: formState.rxcollege }
		: null;
	const isSubmitDisabled = !formState.cpsid.trim() || !formState.rxcollege.trim() || submitting;

	return (
		<div className="fixed inset-0 z-[155] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
			<div className="relative w-full max-w-2xl overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#f8fbff_55%,#f8fafc_100%)] p-7 shadow-[0_32px_120px_rgba(15,23,42,0.24)]">
				<div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.18),transparent_48%),radial-gradient(circle_at_top_right,rgba(14,165,233,0.12),transparent_36%)]" />
				<div className="relative">
					<div className="flex items-start gap-4">
						<span className="mt-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
							<WorkspacePremiumOutlinedIcon />
						</span>
						<div className="flex-1">
							<h2 className="text-2xl font-semibold text-slate-900">Confirm your details</h2>
							<p className="mt-1 text-sm text-slate-600">
								{/* {hasExistingData
									? 'Please confirm your licence number and college before continuing.'
									: 'Please add your licence number and college. You will only need to do this once.'} */}
									Check or update your pharmacist licence number
							</p>
						</div>
					</div>

					<div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-2">
						<div>
							<label htmlFor="pharmacist-cpsid" className="mb-2 block text-sm font-medium text-slate-700">
								Licence Number
							</label>
							<input
								id="pharmacist-cpsid"
								type="text"
								value={formState.cpsid}
								onChange={onCpsidChange}
								placeholder="Enter licence number"
								className="w-full rounded-2xl border border-gray-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-600 focus:ring-4 focus:ring-blue-100"
								autoComplete="off"
							/>
						</div>

						<div>
							<label className="mb-2 block text-sm font-medium text-slate-700">
								College ID
							</label>
							<CreatableSelect
								value={selectedCollegeOption}
								options={collegeOptions}
								styles={collegeSelectStyles}
								isSearchable
								classNamePrefix="react-select"
								placeholder="Select or create a college ID"
								menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
								menuPosition="fixed"
								onChange={(option) => onCollegeChange(option?.value || '')}
								onCreateOption={onCollegeChange}
							/>
						</div>
					</div>

					<div className="mt-5 flex items-center justify-end gap-3">
						<button
							type="button"
							disabled={isSubmitDisabled}
							onClick={onSubmit}
							className={`min-w-[120px] rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
								isSubmitDisabled
									? 'cursor-not-allowed border-slate-300 bg-slate-200 text-slate-600'
									: 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700'
							}`}
						>
							{submitting ? 'Saving...' : hasExistingData ? 'Confirm' : 'Save'}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

const PharmacistVerificationManager = () => {
	const dispatch = useCustomDispatch();
	const location = useCustomLocation();
	const { profileData } = useCustomSelectors();
	const [contextData, setContextData] = useState(null);
	const [formState, setFormState] = useState({ cpsid: '', rxcollege: '' });
	const [showModal, setShowModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		let active = true;

		const loadContext = async () => {
			if (!profileData?.user_id || profileData?.type !== 'pharmacist') {
				if (active) {
					setContextData(null);
					setShowModal(false);
					setFormState({ cpsid: '', rxcollege: '' });
				}
				return;
			}

			try {
				const response = await fetchPharmacistVerificationContext();
				const nextContext = response?.data?.data;

				if (!active) return;

				setContextData(nextContext);
				setFormState({
					cpsid: nextContext?.current_user?.cpsid || '',
					rxcollege: nextContext?.current_user?.rxcollege || '',
				});
				setShowModal(nextContext?.verification_prompt?.should_show === true);
			} catch (error) {
				if (!active) return;
				setShowModal(false);
				safeToast.error(error?.response?.data?.message || 'Unable to load pharmacist verification.');
			}
		};

		loadContext();

		return () => {
			active = false;
		};
	// The verification context belongs to the signed-in pharmacist, not the
	// current route. Loading it only when that identity changes prevents every
	// page navigation from making the same request.
	}, [profileData?.type, profileData?.user_id]);

	useEffect(() => {
		if (location.pathname === routeNames.pharmacy_investigate_rx) {
			setShowModal(false);
			return;
		}

		setShowModal(contextData?.verification_prompt?.should_show === true);
	}, [contextData, location.pathname]);

	const collegeOptions = useMemo(() => {
		return (contextData?.colleges || []).map((college) => ({
			value: college?.value || '',
			label: college?.label || college?.value || '',
		}));
	}, [contextData?.colleges]);

	const hasExistingData = Boolean(
		(contextData?.current_user?.cpsid || '').trim()
		|| (contextData?.current_user?.rxcollege || '').trim()
	);

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			const response = await savePharmacistVerification(formState);
			const nextUser = response?.data?.data?.current_user;

			setContextData((previous) => ({
				...(previous || {}),
				current_user: nextUser || previous?.current_user,
				verification_prompt: {
					should_show: false,
				},
			}));
			setShowModal(false);
			safeToast.success(response?.data?.message || 'Pharmacist verification saved successfully.');
			dispatch(fetchUserProfile());
		} catch (error) {
			safeToast.error(error?.response?.data?.message || 'Unable to save pharmacist verification.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<PharmacistVerificationModal
			open={showModal}
			formState={formState}
			collegeOptions={collegeOptions}
			submitting={submitting}
			hasExistingData={hasExistingData}
			onCpsidChange={(event) => {
				setFormState((previous) => ({
					...previous,
					cpsid: event.target.value,
				}));
			}}
			onCollegeChange={(value) => {
				setFormState((previous) => ({
					...previous,
					rxcollege: value,
				}));
			}}
			onSubmit={handleSubmit}
		/>
	);
};

export default PharmacistVerificationManager;
